import { useLayoutEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { toast } from 'sonner';
import { createAllOrdersDecisions } from './allOrdersDecisions';
import { boundedOrderRead, useOrderWorkspaceSession } from './useOrderWorkspaceSession';
import { isLocalOrder, isProtectedFromDelete } from '@/utils/offlineDeletePolicy';

// TCA-26102002: same-running-tab dispositions of original intake targets.
// No order contents, credentials, durable retry or changes to creation receipts.
const removed = new Set();
const targetKey = (owner, order) => owner && JSON.stringify([
  owner.endpoint, owner.tenant, owner.actor, order.id, isLocalOrder(order),
]);

export default function useRecentOrderDeletion() {
  const session = useOrderWorkspaceSession();
  const latest = useRef(session); latest.current = session;
  const owner = useMemo(() => {
    const key = session.key;
    const controllers = new Set();
    const capture = local => latest.current.key === key ? latest.current.capture(local) : null;
    const decisions = createAllOrdersDecisions(capture,
      (execute, config) => boundedOrderRead(execute, config, controllers),
      { queueCloudDeletes: false });
    return { key, capture, decisions, controllers, active: false };
  }, [session.key]);
  useLayoutEffect(() => {
    owner.active = true;
    owner.decisions.activate();
    return () => {
      owner.active = false;
      owner.decisions.retire();
      owner.controllers.forEach(controller => controller.abort());
    };
  }, [owner]);
  const statuses = useSyncExternalStore(owner.decisions.subscribe, owner.decisions.getSnapshot);
  const identity = order => targetKey(owner.capture(isLocalOrder(order)), order);
  const isRemoved = order => removed.has(identity(order));
  const eligible = order => {
    const captured = owner.capture(isLocalOrder(order));
    return !!captured?.admin && (isLocalOrder(order) || !captured.offline)
      && !isProtectedFromDelete(order) && !isRemoved(order);
  };
  return {
    key: owner.key,
    isRemoved,
    blocked: order => !eligible(order) || owner.decisions.blocked(order.id),
    status: id => session.isAdmin ? statuses.find(status => status.id === id) : undefined,
    async remove(order, current) {
      if (!eligible(order) || !current()) return;
      const captured = owner.capture(isLocalOrder(order));
      const target = identity(order);
      const result = await owner.decisions.run(order, 'delete', {
        current, onAccepted: () => removed.add(target),
      });
      if (!owner.active || !captured?.current() || !current()) return;
      if (result.state === 'accepted') toast.success(result.message);
      else if (result.state === 'refused' || result.state === 'unknown') toast.error(result.message);
    },
    review: id => owner.decisions.review(id),
    acknowledge(order) {
      const captured = owner.capture(isLocalOrder(order));
      const target = targetKey(captured, order);
      if (!target) return;
      owner.decisions.acknowledge(order.id, (evidence, claim) => {
        // An absent local copy never proves absence of a cloud order, even
        // when a replacement receipt carries the same ID.
        if (evidence.absent && claim.endpoint === captured.endpoint && claim.tenant === captured.tenant
          && claim.kind === (isLocalOrder(order) ? 'local' : 'delete')) removed.add(target);
      });
    },
  };
}
